import axios from "axios";
import { isInternal, isProjectReadOnlyRole } from "../../../redux/actions/userAction";
import { getPSGroupByEnv } from "../constants/environment";

/**
 * Returns true if a raw API request object represents a Draft.
 * Checks all status fields case-insensitively because:
 *  - internalStatus is '' (blank) in the DB for Draft
 *  - externalStatus = 'Draft' from cg_lender_request_status
 *  - statusName = 'Draft' from cg_lender_request_status
 * Either externalStatus OR statusName matching 'draft' is sufficient.
 */
export function isDraftRequest(req) {
  const ext = (req?.externalStatus || '').toLowerCase();
  const sn  = (req?.statusName   || '').toLowerCase();
  return ext === 'draft' || sn === 'draft';
}

export async function getAllLenderRequests() {  
  try {
    if (isInternal() || isProjectReadOnlyRole()) {
      const response = await axios.get(
        global.REACT_APP_API_ENDPOINT + `/lender/request/gridRequestFeeds`
      );

      return response.data || [];
    } else {
      const passData = JSON.parse(sessionStorage.getItem("passData"));
      
      const lenderId =
        sessionStorage.getItem("lenderId") ||
        passData?.sellerServicerCoNo ||
        "26546";

      const response = await axios.get(
        global.REACT_APP_API_ENDPOINT + `/lender/request/lenderGridRequestFeeds?lenderId=${lenderId}` 
      );

      return response.data || [];
    }
  } catch (error) {
    console.error("Error fetching lender requests:", error);
    throw error;
  }
}

export async function getLenderRequestsByProject(projectId) {
  let apiPromise;

  if (isInternal() || isProjectReadOnlyRole()) {
    apiPromise = axios.get(
      `${global.REACT_APP_API_ENDPOINT}/lender/request/fetchRequestsByProjectId?timestamp=${Date.now()}`, 
      { params: { projectId } }
    );
  } else {
    // For Lender role, fetch by lenderId and projectId
    //const lenderId = window.localStorage.getItem('lenderId'); 
    const passData = JSON.parse(sessionStorage.getItem("passData"));
    const lenderId = sessionStorage.getItem("lenderId") || passData.sellerServicerCoNo || "12346";

    apiPromise = axios.get(
      `${global.REACT_APP_API_ENDPOINT}/lender/request/fetchRequestsByLenderAndProjectId`, {
        params: { lenderId, projectId }
      }
    );
  }

  let response;
  try {
    response = await apiPromise;
  } catch (err) {
    console.error("Failed to fetch request for project", err);
    throw err;
  }

  if (response?.status !== 200 || !response?.data) {
    console.error("Failed to fetch request for project", response);
    throw new Error('Failed to fetch request for project');
  }

  const requests = response.data || [];

  requests.forEach(request => {
    request.requestStatusId = request.requestStatusId ?? request.projectRequestStatusId;
  });

  return requests;
}

export async function getAllRequestTypes() {
  const response = await axios
    .get(
      global.REACT_APP_API_ENDPOINT + `/requestType/getRequestTypes/`
    )
    .catch(error => {
      throw Error(error);
    });
    
  if (response.status == 200 && response.data?.length) {
    return response.data;
  } else {
    console.error('exception in getAllRequestTypes', response)
    throw response;
  }
}

export async function getAllRequestSubTypes() {
  const response = await axios
    .get(
      global.REACT_APP_API_ENDPOINT + `/requestType/getRequestSubTypes/`
    )
    .catch(error => {
      throw Error(error);
    });

  if (response.status == 200 && response.data) {
    return response.data;
  } else {
    console.error('exception in getAllRequestSubTypes', response);
    throw response; 
  }
}

export async function fetchReviewers() {
  try {
    const response = await axios.get(
      `${global.REACT_APP_API_ENDPOINT}/ldap/getUsersFromCPMByGroup/${getPSGroupByEnv()}`
    );

    if (response.status === 200 && response.data?.length > 0) {
      return Array.from(new Set(response.data)).sort();
    }

    return [];
  } catch (error) {
    console.error("Error fetching reviewers:", error);
    throw error;
  }
}

export async function assignReviewerToRequest(request) {
  try {
    const response = await axios.post(
      `${global.REACT_APP_API_ENDPOINT}/lender/request/assignTo`,
      request
    );

    if (response.status === 200) {
      return true;
    }

    throw new Error('Assignment request failed');
  } catch (error) {
    console.error("Error assigning reviewer:", error);
    throw error;
  }
}

export async function getAdditionalDocuments(trackerId) {
  const response = await axios.get(
    `${global.REACT_APP_API_ENDPOINT}/lender/request/getAdditionalldocumentsByTrackerId?trackerId=${trackerId}`
  );

  if (response?.data) {
    const additionalDocuments = response.data;
    console.log("additionalDocuments:", additionalDocuments);
    return additionalDocuments;
  }
}

export async function getRequiredDocumentsByRequestType(requestTypeName) {
  try {
    const encodedRequestTypeName = encodeURIComponent(requestTypeName);
    const response = await axios.get(
      `${global.REACT_APP_API_ENDPOINT}/lender/request/requestTypeByName/?requestTypeName=${encodedRequestTypeName}`
    );

    if (response.status === 200 && response.data) {
      // Extract documents from nested structure: requestSubTypeDTOS -> requestSubTypeDocsDTOS -> requestDocumentRankingDTO
      const docList = [];
      const uniqueDocNames = new Set();

      if (response.data.requestSubTypeDTOS && Array.isArray(response.data.requestSubTypeDTOS)) {
        response.data.requestSubTypeDTOS.forEach(subType => {
          if (subType.requestSubTypeDocsDTOS && Array.isArray(subType.requestSubTypeDocsDTOS)) {
            subType.requestSubTypeDocsDTOS.forEach(subTypeDoc => {
              // Only include active, required documents that haven't been added yet
              if (subTypeDoc.active && 
                  subTypeDoc.required && 
                  subTypeDoc.requestDocumentRankingDTO &&
                  !uniqueDocNames.has(subTypeDoc.requestDocumentRankingDTO.documentName)) {
                
                uniqueDocNames.add(subTypeDoc.requestDocumentRankingDTO.documentName);
                docList.push(subTypeDoc.requestDocumentRankingDTO);
              }
            });
          }
        });
      }

      return docList;
    }

    return [];
  } catch (error) {
    console.error("Error fetching required documents:", error);
    return [];
  }
}

export function downloadFile(docMapId, fileName) {
  const newTab = globalThis.open('about:blank', '_blank'); 
  axios
    .get(
      global.REACT_APP_API_ENDPOINT + `/docStorage/downloadFile/${docMapId}`,
      { headers: { "Access-Control-Allow-Origin": "*" } }
    )
    .then((response) => {
      const url = response?.data?.fileUrl;
      if (url) {
        if (newTab) newTab.location.href = url;
      } else {
        newTab?.close();
      }
    })
    .catch((error) => {
      newTab?.close();
      console.log(error);
    });
}

export function openDocumentInTab(docId) {
  const newTab = globalThis.open('about:blank', '_blank');
  axios
    .get(global.REACT_APP_API_ENDPOINT + `/docStorage/serveImage/${docId}`, {
      headers: { "Access-Control-Allow-Origin": "*" },
      responseType: 'blob'
    })
    .then((response) => {
      if (response?.data) {
        // Explicit MIME type required - without it the browser renders the raw PDF bytes as text instead of the document.
        const url = globalThis.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        if (newTab) newTab.location.href = url;
      } else {
        newTab?.close();
      }
    })
    .catch((error) => {
      newTab?.close();
      console.log(error);
    });
}

export async function getDashboardStatistics() {
  try {
    const response = await axios.get(
      `${global.REACT_APP_API_ENDPOINT}/lender/request/getDashboardStatistics`
    );

    if (response.status === 200 && response.data) {
      return response.data;
    } else {
      console.error("Failed to fetch dashboard statistics", response);
      return null;
    }
  } catch (error) {
    console.error("Error fetching dashboard statistics:", error);
    return null;
  }
}

/**
 * Bulk-assigns multiple lender requests to a reviewer.
 *
 * @param {Object}   bulkRequest
 * @param {number[]} bulkRequest.requestMasterTrackerIds - IDs of requests to assign
 * @param {string}   bulkRequest.assignTo                - Reviewer username / ID
 * @param {string}   bulkRequest.createdBy               - User performing the assignment
 * @param {string}   [bulkRequest.notes]                 - Optional notes
 * @param {boolean}  [bulkRequest.isEscalated]           - Whether requests are escalated
 * @returns {Promise<{success: boolean, message: string, assignedCount?: number}>}
 */
export async function bulkAssignToReviewers(bulkRequest) {
  let response;
  try {
    response = await axios.post(
      `${global.REACT_APP_API_ENDPOINT}/lender/request/assignTo/bulk`,
      bulkRequest
    );
  } catch (error) {
    console.error(`[BulkAssign] Error (${error.response?.status}): ${error.message}`);
    throw error;
  }

  if (response.status !== 200 || !response.data?.success) {
    const failedIds = response.data?.failedRequestMasterTrackerIds ?? [];
    const message = response.data?.message ?? 'Bulk assignment failed';
    console.error(`[BulkAssign] Non-success response (${response.status}): ${message}`);
    const err = new Error(message);
    err.failedIds = failedIds;
    err.response = response;
    throw err;
  }

  console.log(`[BulkAssign] Success: assigned ${response.data.assignedCount} requests`);
  return response.data;
}

